/**
 * Formatea texto multilinea:
 * - Resalta encabezados tipo "Seccion:"
 * - Intenta traducir labels embebidos (bullets y "Label: Valor") usando getLabel y una clave base
 *
 * @param {string} text
 * @param {Object} options
 * @param {string} options.category - Categoria de traduccion (Summary, VOCSummary, QualityEvaluation, CustomerExperience)
 * @param {string} options.baseKey - Clave base del bloque (ej: "Bloque_1")
 * @param {(key: string, category: string, fallback?: string|null) => string} options.getLabel
 * @returns {import('react').ReactNode}
 */
export function formatTextWithBoldTitles(text, { category, baseKey, getLabel } = {}) {
  if (typeof text !== 'string') return text;

  const lines = text.split('\n');
  let currentSection = null;

  const lookupTranslation = (translationKey) => {
    if (!category || !translationKey || typeof getLabel !== 'function') {
      return null;
    }

    const resolved = getLabel(translationKey, category, null);
    return resolved === translationKey ? null : resolved;
  };

  const translateToken = (translationKey, fallbackText) => {
    const maybe = lookupTranslation(translationKey);
    return maybe ?? fallbackText ?? translationKey;
  };

  return lines.map((line, index) => {
    const trimmed = line.trim();

    if (trimmed.length === 0) {
      return (
        <span key={index}>
          {index < lines.length - 1 && <br />}
        </span>
      );
    }

    const isSectionHeading =
      !trimmed.startsWith('•') &&
      trimmed.endsWith(':') &&
      trimmed.indexOf(':') === trimmed.length - 1;

    if (isSectionHeading) {
      const sectionName = trimmed.slice(0, -1).trim();
      currentSection = sectionName || null;

      const sectionKey = baseKey ? `${baseKey}.${sectionName}` : sectionName;
      const translatedSection = translateToken(sectionKey, sectionName);

      return (
        <span key={index}>
          <strong>{translatedSection}:</strong>
          {index < lines.length - 1 && <br />}
        </span>
      );
    }

    const bulletMatch = line.match(/^(\s*•\s*)([^:]+?)(\s*:\s*)(.*)$/);
    if (bulletMatch) {
      const [, prefix, rawLabel, separator, rest] = bulletMatch;
      const itemKey = (rawLabel ?? '').trim();

      const translationParts = [];
      if (baseKey) translationParts.push(baseKey);
      if (currentSection) translationParts.push(currentSection);
      if (itemKey) translationParts.push(itemKey);
      const translationKey = translationParts.length > 0 ? translationParts.join('.') : null;

      const translatedItem = translationKey
        ? translateToken(translationKey, itemKey)
        : itemKey;

      return (
        <span key={index}>
          {prefix}{translatedItem}{separator}{rest}
          {index < lines.length - 1 && <br />}
        </span>
      );
    }

    const kvMatch = line.match(/^(\s*)([^:]+?)(\s*:\s*)(.*)$/);
    if (kvMatch) {
      const [, indent, rawLabel, separator, rest] = kvMatch;
      const label = (rawLabel ?? '').trim();

      const withSectionKey =
        baseKey && currentSection ? `${baseKey}.${currentSection}.${label}` : null;
      const withoutSectionKey = baseKey ? `${baseKey}.${label}` : null;

      const translatedLabel =
        (withSectionKey ? lookupTranslation(withSectionKey) : null) ??
        (withoutSectionKey ? lookupTranslation(withoutSectionKey) : null) ??
        label;

      return (
        <span key={index}>
          {indent}{translatedLabel}{separator}{rest}
          {index < lines.length - 1 && <br />}
        </span>
      );
    }

    return (
      <span key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </span>
    );
  });
}

export default formatTextWithBoldTitles;
